/**
 * WebsiteIntegration — Settings page that makes wiring the public /intake
 * lead form into the company website copy-paste simple: hosted link, button
 * snippet, iframe embed, raw API example, plus a live "wiring proof" widget
 * (web-lead count + one-click test lead).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Globe, Copy, ExternalLink, Send, CheckCircle2 } from "lucide-react";

const ORIGIN = window.location.origin;
const FORM_URL = `${ORIGIN}/intake`;
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/public/lead-intake`;

const LINK_SNIPPET = `<a href="${FORM_URL}" target="_blank" rel="noopener"
   style="display:inline-block;padding:14px 28px;background:#1d4ed8;color:#fff;
          font-weight:700;text-decoration:none;border-radius:4px;">
  Request a Free Roof Estimate
</a>`;

const IFRAME_SNIPPET = `<iframe src="${FORM_URL}"
        title="SealTech Roof Inquiry"
        style="width:100%;max-width:1080px;height:1050px;border:0;"
        loading="lazy"></iframe>`;

const WEB_HOST_EMAIL = `Subject: Please embed our roof inquiry form on the Contact page

Hi,

We'd like our roof inquiry form embedded on our Contact page
(https://sealtechsolutions.co/contact/).

Please paste this HTML into the page where the form should appear
(a "Custom HTML" / "Embed Code" / "Code" block works on WordPress,
Wix, Squarespace, etc.):

<iframe src="${FORM_URL}"
        title="SealTech Roof Inquiry"
        style="width:100%;max-width:1080px;height:1050px;border:0;"
        loading="lazy"></iframe>

Notes:
- The form is hosted on our system — no styling, scripts, or backend
  work needed on your end. It is mobile-responsive inside the iframe.
- The height (1050px) fits the form fully expanded; adjust if your
  page layout needs it.
- Please keep the src URL exactly as written.

To verify: after publishing, submit the form once with the name
"TEST" — we'll confirm it arrived on our side and then you're done.

Thanks!
SealTech Building Solutions`;

const API_SNIPPET = `POST ${API_URL}
Content-Type: application/json

{
  "name": "Jane Owner",            // required
  "company": "Acme Properties",
  "email": "jane@acme.com",
  "phone": "303-555-0100",
  "property_address": "123 Main St",
  "city": "Denver", "state": "CO", "zip_code": "80202",
  "roof_sqft": 12000,
  "message": "Roof is leaking near the HVAC curb",
  "website_hp": ""                 // honeypot — must stay empty
}`;

const Code = ({ text, label, testId }) => {
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };
  return (
    <div className="relative group">
      <pre className="bg-zinc-950 text-zinc-100 text-[11px] leading-relaxed p-4 rounded-sm overflow-x-auto whitespace-pre">{text}</pre>
      <button
        onClick={copy}
        data-testid={testId}
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 h-7 text-[10px] font-bold uppercase tracking-wider bg-white/10 text-white hover:bg-white/25 rounded-sm"
      >
        <Copy className="w-3 h-3" /> Copy
      </button>
    </div>
  );
};

const Section = ({ step, title, blurb, children }) => (
  <div className="bg-white border border-zinc-200 rounded-sm p-5">
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-full bg-blue-700 text-white text-sm font-black flex items-center justify-center shrink-0">{step}</div>
      <div className="min-w-0 flex-1">
        <div className="font-heading text-base font-black tracking-tight">{title}</div>
        <p className="text-xs text-zinc-500 mt-0.5 mb-3">{blurb}</p>
        {children}
      </div>
    </div>
  </div>
);

export default function WebsiteIntegration() {
  const [deals, setDeals] = useState([]);
  const [sendingTest, setSendingTest] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  useEffect(() => {
    api.get("/settings/lead-intake").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const r = await api.put("/settings/lead-intake", cfg);
      setCfg(r.data);
      toast.success("Auto-reply settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingCfg(false);
    }
  };

  const load = () => api.get("/deals").then((r) => setDeals(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const webLeads = useMemo(
    () => deals.filter((d) => (d.lead_source || "") === "Web Form"),
    [deals]
  );
  const lastLead = webLeads[0]?.created_at || webLeads.map((d) => d.created_at).sort().slice(-1)[0];

  const sendTest = async () => {
    setSendingTest(true);
    try {
      await api.post("/public/lead-intake", {
        name: "TEST — Website Wiring Check",
        company: "Delete me after verifying",
        email: "test@example.com",
        message: "Test lead sent from Settings → Website Leads to prove the pipeline works.",
        website_hp: "",
      });
      toast.success("Test lead sent — check the Projects board (status: Lead), then delete it");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed — the intake API did not accept the lead");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl" data-testid="website-integration-page">
      <div className="flex items-center gap-3 mb-1">
        <Globe className="w-6 h-6 text-blue-700" />
        <h1 className="font-heading text-2xl font-black tracking-tight">Website Leads</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Your public estimate-request form is live. Use any option below on sealtechsolutions.co —
        every submission creates a Contact + Property + Deal (status: Lead) automatically.
      </p>

      {/* Wiring proof */}
      <div className="bg-blue-700 text-white rounded-sm p-5 mb-6 flex flex-wrap items-center gap-6" data-testid="wiring-proof">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-200">Web leads received</div>
          <div className="text-3xl font-black">{webLeads.length}</div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-200">Most recent</div>
          <div className="text-sm font-bold mt-1.5">{lastLead ? String(lastLead).slice(0, 10) : "None yet"}</div>
        </div>
        <button
          onClick={sendTest}
          disabled={sendingTest}
          data-testid="send-test-lead"
          className="ml-auto inline-flex items-center gap-2 px-4 h-10 text-xs font-bold uppercase tracking-wider bg-white text-blue-700 hover:bg-blue-50 rounded-sm disabled:opacity-50"
        >
          <Send className="w-3.5 h-3.5" /> {sendingTest ? "Sending..." : "Send a Test Lead"}
        </button>
      </div>

      <div className="space-y-4">
        <Section step={1} title="Easiest — link to the hosted form" blurb="Add a button or menu link on your website that opens the form. Zero code on your site beyond a link.">
          <div className="flex items-center gap-2 mb-3">
            <input readOnly value={FORM_URL} data-testid="hosted-form-url" className="flex-1 h-9 px-2 border border-zinc-300 rounded-sm text-xs font-mono bg-zinc-50" />
            <button
              onClick={async () => { await navigator.clipboard.writeText(FORM_URL); toast.success("Form link copied"); }}
              data-testid="copy-form-url"
              className="h-9 px-3 text-[10px] font-bold uppercase tracking-wider bg-zinc-950 text-white hover:bg-zinc-800 rounded-sm"
            >
              <Copy className="w-3 h-3 inline -mt-0.5" /> Copy
            </button>
            <a href={FORM_URL} target="_blank" rel="noreferrer" data-testid="open-form-url" className="h-9 px-3 inline-flex items-center text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-950 rounded-sm">
              <ExternalLink className="w-3 h-3 mr-1" /> Open
            </a>
          </div>
          <Code text={LINK_SNIPPET} label="Button snippet" testId="copy-link-snippet" />
        </Section>

        <Section step={2} title="Embed the form directly on a page" blurb='Paste into any HTML block (WordPress "Custom HTML", Wix "Embed Code", Squarespace "Code" block). The form appears inside your page.'>
          <Code text={IFRAME_SNIPPET} label="Embed snippet" testId="copy-iframe-snippet" />
        </Section>

        <Section step={3} title="Sending it to your web host? Copy this email" blurb="A ready-to-send message with the embed code and testing steps — everything the host needs to put the form inside your Contact page.">
          <Code text={WEB_HOST_EMAIL} label="Web host email" testId="copy-webhost-email" />
        </Section>

        <Section step={4} title="For your web developer — post straight to the API" blurb="If you already have a contact form you like, keep it and have it submit to this endpoint. Leads land identically.">
          <Code text={API_SNIPPET} label="API example" testId="copy-api-snippet" />
          <p className="text-[11px] text-zinc-500 mt-2">
            Spam guard: the hidden <code className="font-mono">website_hp</code> honeypot field must be present and empty — bots that fill it are silently dropped.
          </p>
        </Section>
      </div>

      <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-5" data-testid="lead-alerts-card">
        <div className="font-heading text-base font-black tracking-tight">Instant alerts &amp; auto-reply</div>
        <p className="text-xs text-zinc-500 mt-0.5 mb-4">
          Every web lead instantly emails all admins with the details and a link to the deal — always on.
          The prospect auto-acknowledgment below is the ONLY automatic customer email in the system; you control the wording here.
        </p>
        {cfg && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm font-bold text-zinc-800 cursor-pointer">
              <input
                type="checkbox"
                checked={!!cfg.ack_enabled}
                onChange={(e) => setCfg({ ...cfg, ack_enabled: e.target.checked })}
                data-testid="ack-enabled"
                className="accent-blue-700 w-4 h-4"
              />
              Send the prospect an acknowledgment email
            </label>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Subject</label>
              <input value={cfg.ack_subject} onChange={(e) => setCfg({ ...cfg, ack_subject: e.target.value })} data-testid="ack-subject" className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-sm text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Message — {"{name}"} and {"{company}"} fill in automatically</label>
              <textarea value={cfg.ack_body} onChange={(e) => setCfg({ ...cfg, ack_body: e.target.value })} rows={7} data-testid="ack-body" className="mt-1 w-full px-2 py-1.5 border border-zinc-300 rounded-sm text-sm font-mono" />
            </div>
            <button onClick={saveCfg} disabled={savingCfg} data-testid="ack-save" className="px-4 h-9 text-xs font-bold uppercase tracking-wider bg-zinc-950 text-white hover:bg-zinc-800 rounded-sm disabled:opacity-50">
              {savingCfg ? "Saving..." : "Save Auto-Reply"}
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 border border-emerald-300 bg-emerald-50 rounded-sm p-4 flex gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
        <div className="text-xs text-emerald-900">
          <span className="font-bold">Once you deploy,</span> these snippets automatically use your live app address — copy them from this page on the deployed app before pasting into your website.
        </div>
      </div>
    </div>
  );
}
